import React, {forwardRef, useImperativeHandle} from "react";
import {MenuItem, TextField} from "@mui/material";
import {Controller, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import I18nTextField from "../i18n-text-field";
import PasswordInput from "../password-input";
import {AI_ASSISTANT_SEARCH_ENGINE_LIST, isOpenRouter, isVelvet} from "./constants";

const defaultValues = {
  type: "",
  label: "",
  baseUrl: "",
  apiKey: "",
  model: ""
};

const fieldStyle = {margin: "8px 0"};

const AiSearchEngineForm = forwardRef(({initialData, onSubmit}, ref) => {
  const {t} = useTranslation();
  const {handleSubmit, control, watch} = useForm({
    defaultValues: initialData || defaultValues
  });

  const type = watch("type");

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(onSubmit)()
  }));

  return (
    <form>
      {/* Type */}
      <Controller
        name="type"
        control={control}
        rules={{required: t("commons.validation.required")}}
        render={({field}) => (
          <TextField
            {...field}
            required
            select
            fullWidth
            label={t("modules.aiAssistant.aiSettings.searchEngineForm.type")}
            sx={fieldStyle}
            variant="outlined"
          >
            {AI_ASSISTANT_SEARCH_ENGINE_LIST.map(engine => (
              <MenuItem key={engine.id} value={engine.id}>
                {engine.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      {/* Label */}
      <Controller
        name="label"
        control={control}
        rules={{required: t("commons.validation.required")}}
        render={({field, fieldState}) => (
          <I18nTextField
            required
            label={t("modules.aiAssistant.aiSettings.searchEngineForm.label")}
            value={field.value || {}}
            onChange={val => field.onChange(val)}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant="outlined"
            fullWidth
            sx={fieldStyle}
          />
        )}
      />

      {/* Base URL (only velvet and openrouter) */}
      {(isVelvet(type) || isOpenRouter(type)) && (
        <Controller
          name="baseUrl"
          control={control}
          rules={{required: t("commons.validation.required")}}
          render={({field, fieldState}) => (
            <TextField
              {...field}
              required
              fullWidth
              label={t("modules.aiAssistant.aiSettings.searchEngineForm.baseUrl")}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={fieldStyle}
              variant="outlined"
            />
          )}
        />
      )}

      {/* API Key */}
      <Controller
        name="apiKey"
        control={control}
        rules={{required: t("commons.validation.required")}}
        render={({field, fieldState}) => (
          <PasswordInput
            {...field}
            required
            fullWidth
            type="password"
            disableAutocomplete
            label={t("modules.aiAssistant.aiSettings.searchEngineForm.apiKey")}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={fieldStyle}
            variant="outlined"
          />
        )}
      />

      {/* Model (only velvet and openrouter) */}
      {(isVelvet(type) || isOpenRouter(type)) && (
        <Controller
          name="model"
          control={control}
          rules={{required: t("commons.validation.required")}}
          render={({field, fieldState}) => (
            <TextField
              {...field}
              required
              fullWidth
              label={t("modules.aiAssistant.aiSettings.searchEngineForm.model")}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={fieldStyle}
              variant="outlined"
            />
          )}
        />
      )}
    </form>
  );
});

export default AiSearchEngineForm;
